import React, { useState } from 'react';
import CoverSlide from './components/CoverSlide';
import ContentSlide from './components/ContentSlide';
import TwoColumnSlide from './components/TwoColumnSlide';
import TableSlide from './components/TableSlide';
import './styles/Presentation.css';

const ExamplePresentation: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleMouseMove = () => {
    setShowControls(true);
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    hideTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 2000);
  };

  const handleMouseLeave = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    setShowControls(false);
  };

  const commonFooter = {
    estimationCode: 'Example / TMPL-001',
    versionText: 'Version 1.0',
    confidentialText: 'Confidential',
    websiteUrl: 'www.simphony.com.co',
  };

  const slides = [
    <CoverSlide
      key="cover"
      title="Commercial Proposal Template - Example"
      footer={commonFooter}
    />,

    <ContentSlide
      key="content"
      title="About This Template"
      content={
        <div>
          <p>
            This is an example presentation using the commercial proposal template components.
            Each slide is a reusable React component that you can customize for your needs.
          </p>
          <p>
            The template uses <strong>Manrope</strong> for titles and <strong>Inter</strong> for
            body text, providing a modern and professional look.
          </p>
        </div>
      }
      footer={commonFooter}
    />,

    <TwoColumnSlide
      key="columns"
      leftColumn={{
        title: 'Features',
        subtitle: 'What you get with this template',
        backgroundColor: '#f5f5f5',
        items: [
          { number: '01', text: 'Reusable React components' },
          { number: '02', text: 'TypeScript support' },
          { number: '03', text: 'Responsive design' },
          { number: '04', text: 'Professional typography' },
          { number: '05', text: 'Easy customization' },
        ],
      }}
      rightColumn={{
        title: 'Use Cases',
        subtitle: 'Perfect for various scenarios',
        backgroundColor: '#ffffff',
        items: [
          { number: '01', text: 'Business proposals' },
          { number: '02', text: 'Client presentations' },
          { number: '03', text: 'Project pitches' },
          { number: '04', text: 'Team reports' },
          { number: '05', text: 'Product showcases' },
        ],
      }}
      footer={commonFooter}
    />,

    <TableSlide
      key="table"
      title="Sample Pricing Table"
      subtitle="Example of how to display structured data"
      headers={['Service', 'Rate/Hour', 'Monthly (160h)', 'Extra Rate']}
      rows={[
        {
          Service: 'Service A',
          'Rate/Hour': '$ 50.00',
          'Monthly (160h)': '$ 8,000.00',
          'Extra Rate': '$ 60.00',
        },
        {
          Service: 'Service B',
          'Rate/Hour': '$ 75.00',
          'Monthly (160h)': '$ 12,000.00',
          'Extra Rate': '$ 85.00',
        },
      ]}
      footer={commonFooter}
    />,
  ];

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' && currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else if (e.key === 'ArrowLeft' && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    } else if (e.key === 'Escape') {
      window.history.back();
    }
  };

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyDown as any);
    return () => window.removeEventListener('keydown', handleKeyDown as any);
  });

  return (
    <div className="presentation-container">
      {slides[currentSlide]}
      <div
        className="top-controls-zone"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />
      <div
        className={`top-controls ${showControls ? 'visible' : ''}`}
        onMouseMove={handleMouseMove}
      >
        <button className="back-button" onClick={() => window.history.back()}>
          ← Back to Home
        </button>
        <div className="slide-navigation">
          <button
            onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
            disabled={currentSlide === 0}
          >
            ← Previous
          </button>
          <span>
            {currentSlide + 1} / {slides.length}
          </span>
          <button
            onClick={() => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1))}
            disabled={currentSlide === slides.length - 1}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExamplePresentation;
